import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import Form from '../../../../../../components/Form'
import RightEditFormContent from './RightEditFormContent'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { format } from 'date-fns'

import { generalRightPayloadToBE } from '../../RightCreate/RightCreateForm/formatters'

import { updateRight, clearUpdateRight } from '../../../../../../modules/actions/rights'
import {
  updateRightIsLoadingSelector,
  updateRightWasUpdatedSelector,
  updateRightErrorSelector
} from '../../../../../../modules/selectors/rights'
import { choicesAllocationUnitsSelector } from '../../../../../../modules/selectors/choices'
import { selectedPartnershipRightsHolderSelfAccountSelector } from '../../../../../../modules/selectors/app'

import { validationSchema } from './validation'
import {
  ACTION,
  ALLOCATION_UNIT,
  BRAND_KEYWORDS,
  CAMPAIGNS,
  CREATIVE_ASSETS_DATE,
  DELIVERED,
  INCLUDE_LINKED_INSTAGRAM_ACCOUNT,
  LINKED_INSTAGRAM_ACCOUNT,
  LOGO_DATE,
  REQUEST_CREATIVE_ASSETS,
  REQUEST_LOGO,
  RIGHT_TYPE,
  RIGHT_TYPE_DIGITAL,
  RIGHT_TYPE_ONSITE,
  RIGHT_TYPE_POSTS,
  SOCIAL_MEDIA_POST
} from '../../fields'
import { DATES_FORMAT_BE } from '../../../../../../constants/dates'
import { RIGHT_EDIT } from '../../../../../../constants/forms'
import { FACEBOOK_PROVIDER_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { getInitialValues } from './fields'
import { selectedPartnershipSelector } from '../../../../../../modules/selectors/partnerships'

const RightEditForm = ({ editRightId, editRightData }) => {
  const dispatch = useDispatch()

  const rightWasUpdated = useSelector(updateRightWasUpdatedSelector)
  const allocationUnits = useSelector(choicesAllocationUnitsSelector)

  const selectedPartnershipRightsHolderSelfAccount = useSelector(selectedPartnershipRightsHolderSelfAccountSelector)

  const { id: partnershipId } = useSelector(selectedPartnershipSelector)

  const rightsHolderAdServer = selectedPartnershipRightsHolderSelfAccount?.ad_server

  const onSubmit = useCallback(
    values => {
      const generalRightPayload = generalRightPayloadToBE(values)
      const deliveredWasRemoved = editRightData[DELIVERED] && !!values[DELIVERED]

      const filledKeywords = values[BRAND_KEYWORDS].map(item => item.text).filter(item => !!item)

      const formattedCampaigns = values[CAMPAIGNS].filter(campaign => !!campaign.text).map(campaign => ({
        ...campaign,
        external_id: campaign.text,
        provider: campaign.provider || rightsHolderAdServer
      }))

      const includeCreativeAssetsDate = values[REQUEST_CREATIVE_ASSETS] && values[CREATIVE_ASSETS_DATE]
      const includeLogoDate = values[REQUEST_LOGO] && values[LOGO_DATE]
      const includeCampaigns = values[RIGHT_TYPE] === RIGHT_TYPE_ONSITE || values[RIGHT_TYPE] === RIGHT_TYPE_DIGITAL

      const rightType = values[RIGHT_TYPE]

      const payload = {
        // support case when delivered was removed:
        ...(deliveredWasRemoved && { [DELIVERED]: values[DELIVERED] }),
        [ACTION]: values[ACTION],
        [REQUEST_CREATIVE_ASSETS]: values[REQUEST_CREATIVE_ASSETS],
        [CREATIVE_ASSETS_DATE]: includeCreativeAssetsDate
          ? format(new Date(values[CREATIVE_ASSETS_DATE]), DATES_FORMAT_BE)
          : null,
        [REQUEST_LOGO]: values[REQUEST_LOGO],
        [LOGO_DATE]: includeLogoDate ? format(new Date(values[LOGO_DATE]), DATES_FORMAT_BE) : null,
        [BRAND_KEYWORDS]: filledKeywords.length ? filledKeywords : [],
        ...(includeCampaigns && { [CAMPAIGNS]: formattedCampaigns }),
        ...(values[SOCIAL_MEDIA_POST] && {
          pages: [
            {
              // For now provider is always hardcoded to 'facebook'
              provider: FACEBOOK_PROVIDER_PLATFORM,
              page_id: values[SOCIAL_MEDIA_POST],
              right: editRightId
            }
          ]
        })
      }

      // For edit POSTS type right - we must use PUT method,
      // because it has 'pages' field, which includes nested objects
      if (rightType === RIGHT_TYPE_POSTS) {
        const includeInstagramAccount = values[INCLUDE_LINKED_INSTAGRAM_ACCOUNT]
        const instagramAccount = values[LINKED_INSTAGRAM_ACCOUNT]

        dispatch(
          updateRight(
            editRightId,
            {
              ...generalRightPayload,

              ...editRightData,
              partnership: partnershipId,
              [ALLOCATION_UNIT]: 7,
              instagram_accounts: includeInstagramAccount && instagramAccount ? [instagramAccount] : null,
              ...payload
            },
            'PUT'
          )
        )
      } else {
        dispatch(
          updateRight(editRightId, {
            ...generalRightPayload,
            ...payload
          })
        )
      }
    },
    [dispatch, editRightId, editRightData, rightsHolderAdServer, partnershipId]
  )

  const initialValues = useMemo(
    () => getInitialValues({ editedRight: editRightData, allocationUnits }),
    [editRightData, allocationUnits]
  )
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const clearUpdateRightHandler = useCallback(() => dispatch(clearUpdateRight()), [dispatch])

  return (
    <Form
      formName={RIGHT_EDIT}
      formik={purifiedFormik}
      successSubmit={rightWasUpdated}
      isLoadingSelector={updateRightIsLoadingSelector}
      errorSelector={updateRightErrorSelector}
      clearHandler={clearUpdateRightHandler}
    >
      <RightEditFormContent formik={purifiedFormik} />
    </Form>
  )
}

export default RightEditForm
